/**
 * Markdown → HTML rendering for assistant chat replies, backed by `marked`
 * with GFM enabled (tables, strikethrough, task lists, autolinks).
 *
 * Hardening on top of the library defaults:
 * - Raw HTML in the markdown is escaped and shown literally, never emitted
 *   as markup — the only tags in the output are the ones marked generates.
 * - Links render only for http(s)/mailto targets (with target="_blank");
 *   anything else falls back to plain text.
 * - Task-list checkboxes render as text glyphs, since Angular's sanitizer
 *   would strip `<input>` elements anyway.
 *
 * The component binds the result via [innerHTML], where Angular's sanitizer
 * provides a second line of defense.
 */

import { Marked, Tokens } from 'marked';

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

const SAFE_LINK_PROTOCOL = /^(https?:|mailto:)/i;

const chatMarked = new Marked({
    gfm: true,
    // Chat-style replies: a single newline is a line break.
    breaks: true,
    renderer: {
        // Show raw HTML (block or inline) literally instead of injecting it.
        html(token: Tokens.HTML | Tokens.Tag): string {
            return escapeHtml(token.text);
        },
        link(token: Tokens.Link): string {
            const text = this.parser.parseInline(token.tokens);
            if (!SAFE_LINK_PROTOCOL.test(token.href)) return text;
            const title = token.title ? ` title="${escapeHtml(token.title)}"` : '';
            return `<a href="${escapeHtml(token.href)}"${title} target="_blank" rel="noopener noreferrer">${text}</a>`;
        },
        // Angular's [innerHTML] sanitizer strips <input>, so render task-list
        // checkboxes as glyphs instead.
        checkbox(token: Tokens.Checkbox): string {
            return token.checked ? '☑ ' : '☐ ';
        },
    },
});

/** Render assistant-reply markdown to HTML. Safe to bind via [innerHTML]. */
export function renderAssistantMarkdown(markdown: string): string {
    if (!markdown) return '';
    return chatMarked.parse(markdown, { async: false });
}
